import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Flex,
  Icon,
  useColorModeValue,
  VStack,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Spinner,
  Center,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Grid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { FiArrowLeft, FiDownload, FiMoreVertical, FiDollarSign, FiAlertCircle } from "react-icons/fi";
import Card from "components/card/Card";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchInvoiceDetails,
  uploadAdditionalDocuments,
} from "features/invoices/actions";
import { FiImage } from "react-icons/fi";
import PdfIcon from "assets/img/pdf-icon.svg";
import { CheckIcon } from "@chakra-ui/icons";
import AdditionalDocumentUpload from "./AdditionalDocumentUpload";
import { useToastNotification } from "utils/toast-utils";
import DocumentViewerModal from "components/modals/DocumentViewerModal";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatFileSize = (bytes) => {
  const kb = bytes / 1024;
  return `${Math.round(kb)}Kb`;
};

const getFileName = (url) => {
  if (!url) return "Unknown file";
  try {
    return url.split("/").pop() || "Unnamed file";
  } catch (error) {
    return "Unnamed file";
  }
};

const getStatusColor = (status) => {
  const normalizedStatus = status?.toUpperCase();
  if (!normalizedStatus) return "gray.500";

  switch (normalizedStatus) {
    case "PENDING":
      return "orange.500";
    case "UNDER REVIEW":
      return "purple.500";
    case "APPROVED":
      return "green.500";
    case "REJECTED":
      return "red.500";
    case "FUNDED":
      return "teal.500";
    case "INCOMPLETE":
      return "yellow.500";
    case "DELETED":
      return "gray.500";
    case "CLOSED":
      return "blue.500";
    default:
      return "gray.500";
  }
};

const getStatusBgColor = (status) => {
  const normalizedStatus = status?.toUpperCase();
  if (!normalizedStatus) return "gray.100";

  switch (normalizedStatus) {
    case "PENDING":
      return "orange.100";
    case "UNDER REVIEW":
      return "purple.100";
    case "APPROVED":
      return "green.100";
    case "REJECTED":
      return "red.100";
    case "FUNDED":
      return "teal.100";
    case "INCOMPLETE":
      return "yellow.100";
    case "DELETED":
      return "gray.100";
    case "CLOSED":
      return "blue.100";
    default:
      return "gray.100";
  }
};

const getStatusDisplayName = (status) => {
  switch (status) {
    case 'APPROVED':
      return 'Approved';
    case 'UNDER REVIEW':
      return 'Under Review';
    case 'REJECTED':
      return 'Rejected';
    case 'FUNDED':
      return 'Funded';
    case 'INCOMPLETE':
      return 'Incomplete';
    case 'CLOSED':
      return 'Closed';
    case 'DELETED':
      return 'Deleted';
    case 'PENDING':
      return 'Pending';
    case 'DRAFT':
      return 'Draft';
    default:
      return status || 'N/A';
  }
};

const formatAmount = (amount) => {
  if (!amount) return "-";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const PaymentsTab = ({ payments }) => {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.50");

  if (!payments || payments.length === 0) {
    return (
      <Flex 
        direction="column" 
        align="center" 
        justify="center" 
        py={10}
        bg="gray.50"
        borderRadius="md"
      >
        <Icon as={FiDollarSign} w={8} h={8} color="gray.400" mb={3} />
        <Text color="gray.500" fontSize="md">No payments recorded yet</Text>
      </Flex>
    );
  }

  return (
    <Card p="24px">
      <Box overflowX="auto">
        <Table variant="simple" w="full">
          <Thead>
            <Tr>
              <Th
                py={4}
                borderBottom="2px"
                borderColor="gray.200"
                color="gray.600"
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Transaction ID
              </Th>
              <Th
                py={4}
                borderBottom="2px"
                borderColor="gray.200"
                color="gray.600"
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Payment Date
              </Th>
              <Th
                py={4}
                borderBottom="2px"
                borderColor="gray.200"
                color="gray.600"
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Collected Amount
              </Th>
              <Th
                py={4}
                borderBottom="2px"
                borderColor="gray.200"
                color="gray.600"
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Remaining Amount
              </Th>
              <Th
                py={4}
                borderBottom="2px"
                borderColor="gray.200"
                color="gray.600"
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Notes
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {payments.map((payment, index) => (
              <Tr
                key={index}
                _hover={{ bg: hoverBg }}
                transition="background-color 0.2s"
              >
                <Td
                  py={4}
                  color={textColor}
                  fontSize="sm"
                  fontWeight="500"
                  borderBottom="1px"
                  borderColor={borderColor}
                >
                  <Text color="purple.500">
                    {payment.transactionId || 'N/A'}
                  </Text>
                </Td>
                <Td
                  py={4}
                  color={textColor}
                  fontSize="sm"
                  borderBottom="1px"
                  borderColor={borderColor}
                >
                  {formatDate(payment.paymentDate)}
                </Td>
                <Td
                  py={4}
                  borderBottom="1px"
                  borderColor={borderColor}
                >
                  <Text
                    color="green.500"
                    fontSize="sm"
                    fontWeight="600"
                  >
                    {formatAmount(payment.collectedAmount)}
                  </Text>
                </Td>
                <Td
                  py={4}
                  borderBottom="1px"
                  borderColor={borderColor}
                >
                  <Text
                    color="gray.600"
                    fontSize="sm"
                    fontWeight="500"
                  >
                    {formatAmount(payment.remainingAmount)}
                  </Text>
                </Td>
                <Td
                  py={4}
                  color="gray.600"
                  fontSize="sm"
                  borderBottom="1px"
                  borderColor={borderColor}
                >
                  <Text
                    color={textColor}
                    fontSize="sm"
                    fontWeight="500"
                    maxW="250px"
                    whiteSpace="pre-line"
                    wordBreak="break-word"
                  >
                    {payment.notes || '-'}
                  </Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <Flex justify="space-between" align="center" mt={6}>
        <Text color="gray.500" fontSize="sm">
          Total Payments: {payments.length}
        </Text>
        <Text color="gray.500" fontSize="sm">
          Total Collected: {formatAmount(payments.reduce((sum, payment) => sum + payment.collectedAmount, 0))}
        </Text>
      </Flex>
    </Card>
  );
};

const ChargesTab = ({ charges }) => {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.50");

  if (!charges || charges.length === 0) {
    return (
      <Flex 
        direction="column" 
        align="center" 
        justify="center" 
        py={10}
        bg="gray.50"
        borderRadius="md"
      >
        <Icon as={FiAlertCircle} w={8} h={8} color="gray.400" mb={3} />
        <Text color="gray.500" fontSize="md">No charges recorded yet</Text>
      </Flex>
    );
  }

  return (
    <Card p="24px">
      <Box overflowX="auto">
        <Table variant="simple" w="full">
          <Thead>
            <Tr>
              <Th
                py={4}
                borderBottom="2px"
                borderColor="gray.200"
                color="gray.600"
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Charge ID
              </Th>
              <Th
                py={4}
                borderBottom="2px"
                borderColor="gray.200"
                color="gray.600"
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Charge Date
              </Th>
              <Th
                py={4}
                borderBottom="2px"
                borderColor="gray.200"
                color="gray.600"
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Amount
              </Th>
              <Th
                py={4}
                borderBottom="2px"
                borderColor="gray.200"
                color="gray.600"
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Notes
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {charges.map((charge, index) => (
              <Tr
                key={index}
                _hover={{ bg: hoverBg }}
                transition="background-color 0.2s"
              >
                <Td
                  py={4}
                  color={textColor}
                  fontSize="sm"
                  fontWeight="500"
                  borderBottom="1px"
                  borderColor={borderColor}
                >
                  <Text color="purple.500">
                    {charge.chargeId || 'N/A'}
                  </Text>
                </Td>
                <Td
                  py={4}
                  color={textColor}
                  fontSize="sm"
                  borderBottom="1px"
                  borderColor={borderColor}
                >
                  {formatDate(charge.chargeDate)}
                </Td>
                <Td
                  py={4}
                  borderBottom="1px"
                  borderColor={borderColor}
                >
                  <Text
                    color="red.500"
                    fontSize="sm"
                    fontWeight="600"
                  >
                    {formatAmount(charge.chargeAmount)}
                  </Text>
                </Td>
                <Td
                  py={4}
                  color="gray.600"
                  fontSize="sm"
                  borderBottom="1px"
                  borderColor={borderColor}
                >
                  {charge.notes || '-'}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <Flex justify="space-between" align="center" mt={6}>
        <Text color="gray.500" fontSize="sm">
          Total Charges: {charges.length}
        </Text>
        <Text color="gray.500" fontSize="sm">
          Total Amount: {formatAmount(charges.reduce((sum, charge) => sum + charge.chargeAmount, 0))}
        </Text>
      </Flex>
    </Card>
  );
};

const InvoiceDetails = ({ invoiceData, onBack }) => {
  const dispatch = useDispatch();
  const { showToast } = useToastNotification();
  const { selectedInvoice, loading, error } = useSelector(
    (state) => state.invoices
  );
  console.log("🚀 ~ InvoiceDetails ~ selectedInvoice:", selectedInvoice);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  const [isUploading, setIsUploading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  useEffect(() => {
    if (invoiceData?._id) {
      dispatch(fetchInvoiceDetails(invoiceData._id));
    }
  }, [dispatch, invoiceData?._id]);

  if (loading) {
    return (
      <Center h="200px">
        <Spinner size="xl" color="purple.500" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center h="200px">
        <Text color="red.500">Error: {error}</Text>
      </Center>
    );
  }

  // Use selectedInvoice instead of invoiceData prop
  const invoice = selectedInvoice || invoiceData;

  const handleAdditionalDocumentUpload = async (formData) => {
    console.log("🚀 ~ handleAdditionalDocumentUpload ~ formData:", formData);
    try {
      setIsUploading(true);
      await dispatch(
        uploadAdditionalDocuments({
          invoiceId: invoice._id,
          documents: formData,
        })
      ).unwrap();

      showToast("Success", "Documents uploaded successfully", "success");
    } catch (error) {
      showToast(
        "Error",
        error.message || "Failed to upload documents",
        "error"
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDocumentClick = (doc) => {
    setSelectedDocument({
      ...doc,
      fileType: doc.fileType || "image", // Ensure fileType is set
    });
    setIsViewerOpen(true);
  };

  return (
    <Box p="0px">
      <Card p="0px">
        <Flex direction="column" gap={6}>
          {/* Header */}
          <Flex align="center" mb={4}>
            <Icon
              as={FiArrowLeft}
              w={6}
              h={6}
              cursor="pointer"
              onClick={onBack}
              mr={4}
            />
            <Box flex="1">
              <Text fontSize="2xl" fontWeight="bold">
                Invoice Details
              </Text>
              <HStack spacing={2}>
                <Text fontSize="md" color="gray.500">
                  #{invoice?.invoiceNumber}
                </Text>
                <Text
                  px={3}
                  py={1}
                  borderRadius="full"
                  bg={getStatusBgColor(invoice?.status)}
                  color={getStatusColor(invoice?.status)}
                  fontSize="sm"
                  fontWeight="500"
                >
                  {getStatusDisplayName(invoice?.status)}
                </Text>
              </HStack>
            </Box>
          </Flex>

          {/* Summary Cards */}
          <Card p="0" borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
            <Flex gap={4} mb={4}>
              <Card flex="1" p="24px">
                <Text color="gray.500" fontSize="sm" mb={2}>
                  Invoice Amount
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {formatAmount(invoice?.amount)}
                </Text>
              </Card>
              <Card flex="1" p="24px">
                <Text color="gray.500" fontSize="sm" mb={2}>
                  Funded Amount
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {formatAmount(invoice?.fundedAmount)}
                </Text>
              </Card>
              <Card flex="1" p="24px">
                <Text color="gray.500" fontSize="sm" mb={2}>
                  Payment Amount
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {formatAmount(invoice?.paymentAmount)}
                </Text>
              </Card>
              <Card flex="1" p="24px">
                <Text color="gray.500" fontSize="sm" mb={2}>
                  Expected Due Date
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {formatDate(invoice?.expectedDueDate)}
                </Text>
              </Card>
            </Flex>
          </Card>

          {/* Add Tabs */}
          <Tabs variant="soft-rounded" colorScheme="purple" borderRadius="6px">
            <TabList mb="6">
              <Tab borderRadius="6px" _focus={{ boxShadow: "none" }}>
                Details
              </Tab>
              <Tab borderRadius="6px" _focus={{ boxShadow: "none" }}>
                Documents
              </Tab>
              <Tab borderRadius="6px" _focus={{ boxShadow: "none" }}>
                Payments
              </Tab>
              <Tab borderRadius="6px" _focus={{ boxShadow: "none" }}>
                Charges
              </Tab>
              {invoice?.rejections?.length > 0 && (
                <Tab borderRadius="6px" _focus={{ boxShadow: "none" }}>
                  Rejection Reasons
                  <Box
                    as="span"
                    ml={2}
                    px={2}
                    py={0.5}
                    bg="red.100"
                    color="red.500"
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    {invoice.rejections.length}
                  </Box>
                </Tab>
              )}
            </TabList>
            <TabPanels>
              <TabPanel px="0">
                <Flex direction="column" gap={6}>
                  {/* Information Section */}
                  <Card
                    p="24px"
                    borderRadius="8px"
                    boxShadow="0px 0px 12px 0px #0000001A"
                  >
                    <Text
                      fontSize="1rem"
                      fontWeight="600"
                      color="#42505C"
                      mb={4}
                    >
                      Information
                    </Text>
                    <VStack spacing={4} align="stretch">
                      <HStack spacing={4}>
                        <Box flex={1}>
                          <Text color="gray.500" fontSize="sm">
                          Invoice Number
                          </Text>
                          <Text color={textColor} fontWeight="500">
                            {invoice?.referenceNumber || "-"}
                          </Text>
                        </Box>
                        <Box flex={1}>
                          <Text color="gray.500" fontSize="sm">
                            Client
                          </Text>
                          <Text color={textColor} fontWeight="500">
                            {invoice?.debtorName || "-"}
                          </Text>
                        </Box>
                        <Box flex={1}>
                          <Text color="gray.500" fontSize="sm">
                          Debtor
                          </Text>
                          <Text color={textColor} fontWeight="500">
                            {invoice?.debtorName || "-"}
                          </Text>
                        </Box>
                        <Box flex={1}>
                          <Text color="gray.500" fontSize="sm">
                            Invoice Date
                          </Text>
                          <Text color={textColor} fontWeight="500">
                            {formatDate(invoice?.expectedDueDate) || "-"}
                          </Text>
                        </Box>
                      </HStack>
                      <HStack spacing={4}>
                        <Box flex={1}>
                          <Text color="gray.500" fontSize="sm">
                            Funded Date
                          </Text>
                          <Text color={textColor} fontWeight="500">
                            {formatDate(invoice?.fundDate) || "-"}
                          </Text>
                        </Box>
                        <Box flex={1}>
                          <Text color="gray.500" fontSize="sm">
                            Created On
                          </Text>
                          <Text color={textColor} fontWeight="500">
                            {formatDate(invoice?.createdAt) || "-"}
                          </Text>
                        </Box>
                        <Box flex={1}>
                          <Text color="gray.500" fontSize="sm">
                          Payment Amount
                          </Text>
                          <Text color={textColor} fontWeight="500">
                            {formatAmount(invoice?.paymentAmount) || "-"}
                          </Text>
                        </Box>
                        <Box flex={1}>
                          <Text color="gray.500" fontSize="sm">
                            Days Outstanding
                          </Text>
                          <Text color={textColor} fontWeight="500">
                            {(() => {
                              if (!invoice?.expectedDueDate || !invoice?.date || invoice?.status === 'Closed')
                                return "-";
                              const dueDate = new Date(invoice.expectedDueDate);
                              const invoiceDate = new Date();
                              const diffTime = dueDate - invoiceDate;
                              const diffDays = Math.ceil(
                                diffTime / (1000 * 60 * 60 * 24)
                              );
                              return diffDays;
                            })()}
                          </Text>
                        </Box>
                      </HStack>
                      <HStack spacing={4}>
                        <Box flex={1}>
                          <Text color="gray.500" fontSize="sm">
                            Status
                          </Text>
                          <Text color={textColor} fontWeight="500">
                            {getStatusDisplayName(invoice?.status)}
                          </Text>
                        </Box>
                        <Box flex={1}>
                          <Text color="gray.500" fontSize="sm">
                            Payment Status
                          </Text>
                          <Text color={textColor} fontWeight="500">
                            {(() => {
                              const amount = parseFloat(invoice?.amount) || 0;
                              const remainingAmount =
                                parseFloat(invoice?.remainingAmount) || 0;

                              if (remainingAmount === amount) return "Pending";
                              if (remainingAmount === 0) return "Fully Paid";
                              if (remainingAmount < amount)
                                return "Partially Paid";
                              return "Unknown";
                            })()}
                          </Text>
                        </Box>
                        <Box flex={1}>
                          <Text color="gray.500" fontSize="sm">
                            Paid On
                          </Text>
                          <Text color={textColor} fontWeight="500">
                            {formatDate(invoice?.paidOn) || "-"}
                          </Text>
                        </Box>
                        <Box flex={1} />
                      </HStack>
                      {/* Add Notes Section */}
                      {invoice?.notes ? (
                        <Box mt={6}>
                          <Text color="gray.500" fontSize="sm" mb={2} display="flex" alignItems="center" gap={2}>
                            <Icon as={FiImage} color="purple.400" boxSize={4} />
                            Notes
                          </Text>
                          <Box
                            p={4}
                            bg="purple.50"
                            borderLeft="4px solid"
                            borderColor="purple.300"
                            borderRadius="md"
                            borderWidth="1px"
                            maxH="120px"
                            overflowY="auto"
                          >
                            <Text color={textColor} whiteSpace="pre-wrap">
                              {invoice.notes}
                            </Text>
                          </Box>
                        </Box>
                      ) : null}
                    </VStack>
                  </Card>

                  {/* Amount Details Section */}
                  <Card
                    p="24px"
                    borderRadius="8px"
                    boxShadow="0px 0px 12px 0px #0000001A"
                  >
                    <Text
                      fontSize="1rem"
                      fontWeight="600"
                      color="#42505C"
                      mb={4}
                    >
                      Amount Details
                    </Text>
                    <HStack spacing={4}>
                      <Box flex={1}>
                        <Text color="gray.500" fontSize="sm">
                          Invoice Amount
                        </Text>
                        <Text color={textColor} fontWeight="500">
                          {formatAmount(invoice?.amount)}
                        </Text>
                      </Box>
                      <Box flex={1}>
                        <Text color="gray.500" fontSize="sm">
                          Funded Amount
                        </Text>
                        <Text color={textColor} fontWeight="500">
                          {formatAmount(invoice?.fundedAmount)}
                        </Text>
                      </Box>
                      <Box flex={1}>
                        <Text color="gray.500" fontSize="sm">
                          Payment Amount
                        </Text>
                        <Text color={textColor} fontWeight="500">
                          {formatAmount(invoice?.paymentAmount)}
                        </Text>
                      </Box>
                      <Box flex={1}>
                        <Text color="gray.500" fontSize="sm">
                          Collected Amount
                        </Text>
                        <Text color={textColor} fontWeight="500">
                          {formatAmount(invoice?.amount - invoice?.remainingAmount)}
                        </Text>
                      </Box>
                      <Box flex={1}>
                        <Text color="gray.500" fontSize="sm">Escrow Amount</Text>
                        <Text color={textColor} fontWeight="500">
                          {formatAmount(invoice?.escrowAmount)}
                        </Text>
                      </Box>
                    </HStack>
                  </Card>

                  {/* Activity Section */}
                  <Card
                    p="24px"
                    borderRadius="8px"
                    boxShadow="0px 0px 12px 0px #0000001A"
                  >
                    <Text
                      fontSize="1rem"
                      fontWeight="600"
                      color="#42505C"
                      mb={4}
                    >
                      Activity
                    </Text>
                    <VStack spacing={4} align="stretch">
                      {invoice?.activities?.map((activity, index, array) => (
                        <Flex key={index} gap={4}>
                          <Box w="24px" position="relative">
                            {index === 0 ? (
                              <Box
                                w="24px"
                                h="24px"
                                borderRadius="full"
                                bg="green.100"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Box
                                  w="8px"
                                  h="8px"
                                  borderRadius="full"
                                  bg="green.500"
                                />
                              </Box>
                            ) : (
                              <Box
                                w="24px"
                                h="24px"
                                borderRadius="full"
                                bg="green.500"
                                border="2px solid"
                                borderColor="green.500"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <CheckIcon w={3} h={3} color="white" />
                              </Box>
                            )}
                            {index !== array.length - 1 && (
                              <Box
                                position="absolute"
                                left="11px"
                                top="24px"
                                width="2px"
                                height="calc(100% + 16px)"
                                bg="gray.200"
                              />
                            )}
                          </Box>
                          <Box flex="1">
                            <Text color={textColor} fontWeight="500">
                              {activity.description}
                            </Text>
                            <Text color="gray.500" fontSize="sm">
                              {activity.date}
                            </Text>
                            {activity.reason && (
                              <Text color="gray.500" fontSize="sm" mt={1}>
                                Reason: {activity.reason}
                              </Text>
                            )}
                          </Box>
                        </Flex>
                      ))}
                    </VStack>
                  </Card>
                </Flex>
              </TabPanel>
              <TabPanel px="0">
                {invoice?.status === "ADDITIONAL_DOCUMENT_REQUIRED" && (
                  <Card
                    p="24px"
                    borderRadius="8px"
                    boxShadow="0px 0px 12px 0px #0000001A"
                    mb={6}
                  >
                    <AdditionalDocumentUpload
                      onUpload={handleAdditionalDocumentUpload}
                      isLoading={isUploading}
                    />
                  </Card>
                )}
                <Card
                  p="24px"
                  borderRadius="8px"
                  boxShadow="0px 0px 12px 0px #0000001A"
                >
                  <Text fontSize="1rem" fontWeight="600" color="#42505C" mb={6}>
                    Documents
                  </Text>
                  <VStack spacing={4} align="stretch">
                    {invoice?.documents?.map((doc) => (
                      <Flex
                        key={doc._id}
                        p={4}
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                        align="center"
                        cursor="pointer"
                        onClick={() => handleDocumentClick(doc)}
                        _hover={{ bg: "gray.50" }}
                      >
                        {doc.fileType === "pdf" ? (
                          <Box
                            w="40px"
                            h="40px"
                            mr={4}
                            flexShrink={0}
                            display="flex"
                            alignItems="center"
                          >
                            <img
                              src={PdfIcon}
                              alt="PDF Document"
                              style={{ width: "34px" }}
                            />
                          </Box>
                        ) : doc.fileType === "image" ? (
                          <Box
                            w="40px"
                            h="40px"
                            borderRadius="md"
                            overflow="hidden"
                            mr={4}
                            flexShrink={0}
                          >
                            <img
                              src={doc.url}
                              alt={getFileName(doc.url)}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        ) : (
                          <Icon
                            as={FiImage}
                            boxSize={6}
                            color="gray.500"
                            mr={4}
                          />
                        )}
                        <Box flex="1">
                          <Text fontWeight="500" color={textColor}>
                            {getFileName(doc.url)}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            Uploaded{" "}
                            {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            }) : '-'}
                          </Text>
                        </Box>
                        <HStack spacing={4}>
                          <Text
                            color="green.500"
                            fontSize="sm"
                            fontWeight="500"
                          >
                            {doc.status || "Completed"}
                          </Text>
                          <Text color="gray.500" fontSize="sm">
                            {doc.fileType?.toUpperCase() || "UNKNOWN"}
                          </Text>
                          {/* <Text color="gray.500" fontSize="sm">
                            {formatFileSize(doc.size || 200 * 1024)}
                          </Text> */}
                          <IconButton
                            icon={<FiDownload />}
                            variant="ghost"
                            size="sm"
                            aria-label="Download"
                            onClick={(e) => {
                              e.stopPropagation();
                              doc.url && window.open(doc.url, "_blank");
                            }}
                            isDisabled={!doc.url}
                          />
                          {/* <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<FiMoreVertical />}
                              variant="ghost"
                              size="sm"
                              aria-label="More options"
                            />
                            <MenuList>
                              <MenuItem>View Details</MenuItem>
                              <MenuItem>Delete</MenuItem>
                            </MenuList>
                          </Menu> */}
                        </HStack>
                      </Flex>
                    ))}
                    {(!invoice?.documents ||
                      invoice.documents.length === 0) && (
                      <Text color="gray.500" textAlign="center">
                        No documents available
                      </Text>
                    )}
                  </VStack>
                </Card>
              </TabPanel>
              <TabPanel px="0">
                <Card p="24px" borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
                  <Text fontSize="1rem" fontWeight="600" color="#42505C" mb={6}>
                    Payment History
                  </Text>
                  <PaymentsTab payments={invoice?.payments} />
                </Card>
              </TabPanel>
              <TabPanel px="0">
                <Card p="24px" borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
                  <Text fontSize="1rem" fontWeight="600" color="#42505C" mb={6}>
                    Charges History
                  </Text>
                  <ChargesTab charges={invoice?.lateCharges} />
                </Card>
              </TabPanel>
              {/* Add Rejection Reasons Panel */}
              {invoice?.rejections?.length > 0 && (
                <TabPanel px="0">
                  <Card
                    p="24px"
                    borderRadius="8px"
                    boxShadow="0px 0px 12px 0px #0000001A"
                  >
                    <Text
                      fontSize="1rem"
                      fontWeight="600"
                      color="#42505C"
                      mb={6}
                    >
                      Rejection History
                    </Text>
                    <VStack spacing={4} align="stretch">
                      {invoice.rejections.map((rejection, index) => (
                        <Flex
                          key={rejection._id}
                          p={4}
                          borderWidth="1px"
                          borderColor={borderColor}
                          borderRadius="md"
                          bg="red.50"
                          position="relative"
                        >
                          <Box
                            position="absolute"
                            left="-1px"
                            top="0"
                            bottom="0"
                            width="4px"
                            bg="red.500"
                            borderTopLeftRadius="md"
                            borderBottomLeftRadius="md"
                          />
                          <VStack align="stretch" spacing={2} flex="1">
                            <Text color="red.700" fontWeight="500">
                              {rejection.reason}
                            </Text>
                            <HStack spacing={4} color="gray.500" fontSize="sm">
                              <Text>
                                Rejected on{" "}
                                {new Date(
                                  rejection.createdAt
                                ).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}
                              </Text>
                              <Text>
                                at{" "}
                                {new Date(
                                  rejection.createdAt
                                ).toLocaleTimeString()}
                              </Text>
                            </HStack>
                          </VStack>
                        </Flex>
                      ))}
                    </VStack>
                  </Card>
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </Flex>
      </Card>
      <DocumentViewerModal
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        document={selectedDocument}
      />
    </Box>
  );
};

export default InvoiceDetails;
